import {msalInstance} from './msalConfig';
import {useToken} from '@/core/utils/token';
import type {AccountInfo, AuthenticationResult} from "@azure/msal-browser";
import {useHttp} from "@/core/http";
import {useAuth} from "@/plugins/auth";

interface MicrosoftLoginResponse {
    message: string;
    token: string;
    token_type: string;
}

const tokenHelper = useToken();

export function useMicrosoftAuthService() {

    const initialize = async () => {
        try {
            await msalInstance.initialize();
        } catch (error) {
            console.error('MSAL initialization error:', error);
            throw error;
        }
    };

    const login = async () => {
        try {
            if (!msalInstance) {
                throw new Error('MSAL not initialized.');
            }

            await msalInstance.loginRedirect({
                scopes: ['User.Read', 'email', 'profile', 'openid']
            });
        } catch (error) {
            console.error('Microsoft login error:', error);
            throw error;
        }
    };

    const handleRedirect = async (): Promise<boolean> => {
        try {
            const result: AuthenticationResult | null = await msalInstance.handleRedirectPromise();

            if (!result) {
                return false;
            }

            const account: AccountInfo | undefined = result.account;

            // Get access token for Microsoft Graph API
            const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: ['User.Read'],
                account: account
            });

            // Prepare payload for backend
            const payload = {
                access_token: tokenResponse.accessToken,
                email: account.username,
            };

            // Call backend to exchange Microsoft token for application JWT
            const data: MicrosoftLoginResponse = await useHttp().post('/v3/auth/login-with-microsoft', payload);

            // Store the application JWT token
            tokenHelper.setToken(data);

            // Fetch and populate user data
            const auth = useAuth();
            await auth.auth();

            return true;
        } catch (error) {
            console.error('Microsoft redirect handling error:', error);
            tokenHelper.clearToken();
            throw error;
        }
    };

    return {
        handleRedirect,
        initialize,
        login,
    };
}
